import React from "react";
import Icon from "../../../components/AppIcon";

const DIFFICULTY = {
  1: { label: "Beginner", bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", border: "border-emerald-200" },
  2: { label: "Easy",     bg: "bg-green-100",   text: "text-green-700",   dot: "bg-green-500",   border: "border-green-200" },
  3: { label: "Moderate", bg: "bg-blue-100",    text: "text-blue-700",    dot: "bg-blue-500",    border: "border-blue-200" },
  4: { label: "Challenging", bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500",  border: "border-orange-200" },
  5: { label: "Expert",   bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500",     border: "border-red-200" },
};

const DifficultyBadge = ({ level }) => {
  const d = DIFFICULTY[level] ?? DIFFICULTY[3];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${d.bg} ${d.text} ${d.border} border`}>
      <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`} />
      {d.label}
    </span>
  );
};

const GridCard = ({ trek, onViewDetails }) => {
  const d = trek.trekData;

  return (
    <article
      onClick={() => onViewDetails(trek)}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-primary/20 transition-all duration-300 cursor-pointer flex flex-col"
    >
      <div className="relative h-52 overflow-hidden flex-shrink-0">
        <img
          src={d.heroImage}
          alt={d.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-primary rounded-full">
            {d.category}
          </span>
          {d.customizable && (
            <span className="px-2.5 py-1 bg-accent/90 backdrop-blur-sm text-xs font-semibold text-white rounded-full">
              Customizable
            </span>
          )}
        </div>

        <span className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-medium rounded-full">
          <Icon name="Clock" size={11} />
          {d.duration} days
        </span>

        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <div className="flex items-center gap-1 text-white/90 text-xs font-medium">
            <Icon name="TrendingUp" size={12} />
            {d.maxAltitude}
          </div>

        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <DifficultyBadge level={d.difficulty} />
        </div>

        <h3 className="font-headline font-bold text-base text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors">
          {d.name}
        </h3>

        <p className="text-text-secondary text-sm line-clamp-2 mb-3 flex-1">
          {d.subtitle}
        </p>

        {d.region && (
          <div className="flex items-center gap-1.5 mb-3 text-xs text-gray-500">
            <Icon name="MapPin" size={12} className="text-primary/70 flex-shrink-0" />
            <span>{d.region}</span>
            {d.bestSeason && (
              <>
                <span className="text-gray-300">·</span>
                <Icon name="Calendar" size={12} className="text-primary/70 flex-shrink-0" />
                <span>{d.bestSeason}</span>
              </>
            )}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 mt-auto">
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetails(trek); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-background text-foreground text-[11px] font-bold uppercase tracking-[0.2em] rounded-sm border border-foreground/20 hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300"
          >
            View Trek
            <Icon name="ArrowRight" size={12} />
          </button>
        </div>
      </div>
    </article>
  );
};

const ListCard = ({ trek, onViewDetails }) => {
  const d = trek.trekData;
  const diff = DIFFICULTY[d.difficulty] ?? DIFFICULTY[3];

  return (
    <article
      onClick={() => onViewDetails(trek)}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-100 hover:border-primary/20 transition-all duration-300 cursor-pointer flex gap-0"
    >
      <div className="relative w-48 sm:w-56 flex-shrink-0 overflow-hidden">
        <img
          src={d.heroImage}
          alt={d.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <span className="absolute top-3 left-3 px-2 py-0.5 bg-white/90 text-xs font-semibold text-primary rounded-full">
          {d.category}
        </span>
        <span className="absolute bottom-3 left-3 text-white text-xs font-medium">
          {d.duration} days
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-headline font-bold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {d.name}
          </h3>
          <DifficultyBadge level={d.difficulty} />
        </div>

        <p className="text-text-secondary text-sm line-clamp-2 mb-3">{d.subtitle}</p>

        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-500 mb-4">
          <span className="flex items-center gap-1.5">
            <Icon name="MapPin" size={12} className="text-primary" />
            {d.region}
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="TrendingUp" size={12} className="text-primary" />
            {d.maxAltitude}
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="Calendar" size={12} className="text-primary" />
            {d.bestSeason}
          </span>
        </div>

        <div className="mt-auto pt-3 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetails(trek); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-background text-foreground text-[11px] font-bold uppercase tracking-[0.2em] rounded-sm border border-foreground/20 hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300"
          >
            View Trek
            <Icon name="ArrowRight" size={12} />
          </button>
        </div>
      </div>
    </article>
  );
};

const TrekCard = ({ trek, onViewDetails, viewMode = "grid" }) => {
  if (viewMode === "list") return <ListCard trek={trek} onViewDetails={onViewDetails} />;
  return <GridCard trek={trek} onViewDetails={onViewDetails} />;
};

export default TrekCard;
